import React, { useState, useEffect, useMemo } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { operationApi } from '../../services/api';
import { ClipboardCheck, UserCheck, Plus, Edit, Trash2, RotateCcw, XCircle, ChevronDown, ChevronRight } from 'lucide-react';

const ASSIGN_STATUSES = ['active', 'returned', 'lost', 'damaged'];
const statusColors = { active: 'bg-blue-100 text-blue-700', returned: 'bg-green-100 text-green-700', lost: 'bg-red-100 text-red-700', damaged: 'bg-yellow-100 text-yellow-700' };

export default function OperationAssignmentsPage() {
  const [assignments, setAssignments] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', user_id: '', search: '' });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [workers, setWorkers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [expandedWorkers, setExpandedWorkers] = useState({});
  const [form, setForm] = useState({
    user_id: '', items: [{ inventory_item_id: '', quantity: 1 }],
    assigned_date: '', expected_return_date: '', status: 'active', notes: '',
  });

  const fetchAssignments = async (page = 1) => {
    setLoading(true);
    try {
      const params = { per_page: 200, page };
      if (filters.status) params.status = filters.status;
      if (filters.user_id) params.user_id = filters.user_id;
      if (filters.search) params.search = filters.search;
      const res = await operationApi.assignments.list(params);
      const d = res.data.data;
      setAssignments(d?.data || d || []);
      setPagination({ current_page: d?.current_page || 1, last_page: d?.last_page || 1, total: d?.total || 0 });
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const loadWorkersAndInventory = async () => {
    try {
      const [wRes, iRes] = await Promise.all([
        operationApi.profiles.list({ per_page: 100 }),
        operationApi.inventoryItems.list({ per_page: 100 }),
      ]);
      setWorkers(wRes.data.data?.data || wRes.data.data || []);
      setInventory(iRes.data.data?.data || iRes.data.data || []);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchAssignments(1); }, [filters]);

  // Group assignments by worker
  const groupedByWorker = useMemo(() => {
    const groups = {};
    assignments.forEach(a => {
      const uid = a.user_id || 'unknown';
      if (!groups[uid]) {
        groups[uid] = {
          user_id: uid,
          user: a.user,
          items: [],
          latest_date: a.assigned_date,
          statuses: new Set(),
        };
      }
      groups[uid].items.push(a);
      groups[uid].statuses.add(a.status);
      if (a.assigned_date > groups[uid].latest_date) groups[uid].latest_date = a.assigned_date;
    });
    return Object.values(groups);
  }, [assignments]);

  const toggleExpand = (uid) => {
    setExpandedWorkers(prev => ({ ...prev, [uid]: !prev[uid] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.user_id) return alert('Pilih pekerja');
    if (form.items.length === 0 || form.items.some(i => !i.inventory_item_id)) return alert('Pilih minimal 1 item inventaris');
    setSaving(true);
    try {
      const promises = form.items.map(item => {
        const payload = {
          user_id: form.user_id,
          inventory_item_id: item.inventory_item_id,
          quantity: item.quantity || 1,
          assigned_date: form.assigned_date,
          expected_return_date: form.expected_return_date,
          status: form.status,
          notes: form.notes,
        };
        if (editing) {
          return operationApi.assignments.update(editing.id, payload);
        } else {
          return operationApi.assignments.create(payload);
        }
      });
      await Promise.all(promises);
      setShowForm(false); setEditing(null);
      setForm({ user_id: '', items: [{ inventory_item_id: '', quantity: 1 }], assigned_date: '', expected_return_date: '', status: 'active', notes: '' });
      fetchAssignments(pagination.current_page);
    } catch (err) {
      const msg = err.response?.data?.message || 'Gagal menyimpan';
      alert(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally { setSaving(false); }
  };

  const handleEdit = (a) => {
    setEditing(a);
    setForm({
      user_id: a.user_id || '',
      items: [{ inventory_item_id: a.inventory_item_id || '', quantity: a.quantity || 1 }],
      assigned_date: a.assigned_date || '',
      expected_return_date: a.expected_return_date || '',
      status: a.status || 'active', notes: a.notes || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus assignment ini?')) return;
    try { await operationApi.assignments.delete(id); fetchAssignments(pagination.current_page); } catch (err) { alert('Gagal menghapus'); }
  };

  const openNewForm = async () => {
    await loadWorkersAndInventory();
    setEditing(null);
    setForm({ user_id: '', items: [{ inventory_item_id: '', quantity: 1 }], assigned_date: '', expected_return_date: '', status: 'active', notes: '' });
    setShowForm(true);
  };

  const openEditForm = async (a) => {
    await loadWorkersAndInventory();
    handleEdit(a);
  };

  const addItemRow = () => setForm({ ...form, items: [...form.items, { inventory_item_id: '', quantity: 1 }] });
  const removeItemRow = (idx) => {
    if (form.items.length <= 1) return;
    setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  };
  const updateItemRow = (idx, field, value) => {
    const updated = [...form.items];
    updated[idx] = { ...updated[idx], [field]: value };
    setForm({ ...form, items: updated });
  };

  const getOverallStatus = (statuses) => {
    const arr = Array.from(statuses);
    if (arr.every(s => s === 'returned')) return 'returned';
    if (arr.some(s => s === 'lost')) return 'lost';
    if (arr.some(s => s === 'damaged')) return 'damaged';
    return 'active';
  };

  const pages = [];
  for (let i = Math.max(1, pagination.current_page - 2); i <= Math.min(pagination.last_page, pagination.current_page + 2); i++) pages.push(i);

  return (
    <AdminLayout active="Assignment">
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-lg font-semibold text-gray-800 mb-1 flex items-center gap-2"><ClipboardCheck size={24} /> Assignment</h1>
          <button onClick={openNewForm} className="px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-dark text-sm flex items-center gap-2"><Plus size={16} /> Assign Item</button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Cari (Nama Pekerja / Item)</label>
              <input type="text" placeholder="Cari nama pekerja atau item..." value={filters.search} onChange={e => setFilters({ ...filters, search: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">Semua</option>
                {ASSIGN_STATUSES.map(s => <option key={s} value={s}>{s === 'active' ? 'Aktif' : s === 'returned' ? 'Kembali' : s === 'lost' ? 'Hilang' : 'Rusak'}</option>)}
              </select>
            </div>
          </div>
        </div>

        {loading ? <div className="text-center py-10 text-gray-500">Loading...</div> : (
          <>
            <div className="bg-white rounded-lg shadow overflow-hidden mb-4">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-8"></th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pekerja</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item Diassign</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Total Item</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tgl Assign</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {groupedByWorker.map(group => {
                    const overallStatus = getOverallStatus(group.statuses);
                    const isExpanded = expandedWorkers[group.user_id];
                    return (
                      <React.Fragment key={group.user_id}>
                        {/* Main row - worker summary */}
                        <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => toggleExpand(group.user_id)}>
                          <td className="px-4 py-3">
                            {isExpanded ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 bg-brand/10 rounded-full flex items-center justify-center text-navy font-bold text-[10px] flex-shrink-0">
                                {group.user?.operation_profile?.full_name?.charAt(0) || group.user?.name?.charAt(0) || '?'}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{group.user?.operation_profile?.full_name || group.user?.name || 'Unknown'}</p>
                                <p className="text-xs text-gray-400">{group.user?.operation_profile?.employee_id || '-'}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {group.items.slice(0, 3).map(a => (
                                <span key={a.id} className="inline-flex items-center px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded-full">
                                  {a.inventory_item?.name || 'Item #'+a.inventory_item_id}
                                  <span className="ml-1 text-gray-400">×{a.quantity}</span>
                                </span>
                              ))}
                              {group.items.length > 3 && <span className="text-xs text-gray-400">+{group.items.length - 3} lagi</span>}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center text-sm font-bold text-gray-900">{group.items.length}</td>
                          <td className="px-4 py-3 text-xs text-gray-500">
                            {group.latest_date ? new Date(group.latest_date).toLocaleDateString('id-ID') : '-'}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs rounded-full font-medium ${statusColors[overallStatus] || ''}`}>
                              {overallStatus === 'active' ? 'Aktif' : overallStatus === 'returned' ? 'Kembali' : overallStatus === 'lost' ? 'Hilang' : 'Rusak'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right space-x-1" onClick={e => e.stopPropagation()}>
                            <button onClick={() => openNewForm()} className="text-brand hover:text-brand-dark text-xs"><Plus size={14} /></button>
                          </td>
                        </tr>
                        {/* Expanded detail rows */}
                        {isExpanded && group.items.map(a => (
                          <tr key={a.id} className="bg-gray-50">
                            <td className="px-4 py-2"></td>
                            <td className="px-4 py-2"></td>
                            <td className="px-4 py-2 pl-10">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-800">{a.inventory_item?.name || 'Item #'+a.inventory_item_id}</span>
                                <span className="text-xs text-gray-400">{a.inventory_item?.category} · {a.inventory_item?.unit}</span>
                              </div>
                            </td>
                            <td className="px-4 py-2 text-center text-sm font-bold text-gray-700">×{a.quantity}</td>
                            <td className="px-4 py-2 text-xs text-gray-500">
                              {a.assigned_date ? new Date(a.assigned_date).toLocaleDateString('id-ID') : '-'}
                              {a.expected_return_date && <span className="block text-gray-400">s/d {new Date(a.expected_return_date).toLocaleDateString('id-ID')}</span>}
                            </td>
                            <td className="px-4 py-2">
                              <span className={`px-2 py-1 text-xs rounded-full font-medium ${statusColors[a.status] || ''}`}>
                                {a.status === 'active' ? 'Aktif' : a.status === 'returned' ? 'Kembali' : a.status === 'lost' ? 'Hilang' : 'Rusak'}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-right space-x-1">
                              <button onClick={() => openEditForm(a)} className="text-blue-600 hover:text-blue-800 text-xs"><Edit size={14} /></button>
                              <button onClick={() => handleDelete(a.id)} className="text-red-600 hover:text-red-800 text-xs"><Trash2 size={14} /></button>
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                  {groupedByWorker.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">Belum ada assignment</td></tr>}
                </tbody>
              </table>
            </div>
            {pagination.last_page > 1 && (
              <div className="flex items-center justify-between bg-white rounded-lg shadow px-4 py-3">
                <span className="text-sm text-gray-500">Total {pagination.total} · Halaman {pagination.current_page} dari {pagination.last_page}</span>
                <div className="flex items-center gap-1">
                  <button disabled={pagination.current_page === 1} onClick={() => fetchAssignments(pagination.current_page - 1)} className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-30">«</button>
                  {pages.map(p => <button key={p} onClick={() => fetchAssignments(p)} className={`px-3 py-1 text-sm rounded ${p === pagination.current_page ? 'bg-brand text-white' : 'border border-gray-300'}`}>{p}</button>)}
                  <button disabled={pagination.current_page === pagination.last_page} onClick={() => fetchAssignments(pagination.current_page + 1)} className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-30">»</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><UserCheck size={20} /> {editing ? 'Edit Assignment' : 'Assign Item ke Pekerja'}</h2>
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Pekerja *</label>
                <select required value={form.user_id} onChange={e => setForm({ ...form, user_id: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  <option value="">- Pilih Pekerja -</option>
                  {workers.filter(w => w.status === 'active').map(w => <option key={w.user_id} value={w.user_id}>{w.full_name} ({w.employee_id})</option>)}
                </select>
              </div>

              {/* Multiple Items */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-xs font-medium text-gray-600">Item Inventaris *</label>
                  <button type="button" onClick={addItemRow} className="text-brand text-xs hover:text-brand-dark font-medium">+ Tambah Item</button>
                </div>
                <div className="space-y-2">
                  {form.items.map((item, idx) => {
                    const selectedItem = inventory.find(i => i.id === parseInt(item.inventory_item_id));
                    return (
                      <div key={idx} className="flex items-center gap-2 bg-gray-50 p-2 rounded-lg">
                        <div className="flex-1">
                          <select required value={item.inventory_item_id} onChange={e => updateItemRow(idx, 'inventory_item_id', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                            <option value="">- Pilih Item -</option>
                            {inventory.filter(i => i.status !== 'out_of_stock' && i.quantity > 0).map(i => <option key={i.id} value={i.id}>{i.name} (Stok: {i.quantity} {i.unit})</option>)}
                          </select>
                          {selectedItem && <p className="text-xs text-gray-400 mt-0.5 ml-1">Stok: {selectedItem.quantity} {selectedItem.unit}</p>}
                        </div>
                        <div className="w-20">
                          <input type="number" required min="1" max={selectedItem?.quantity || 999} value={item.quantity} onChange={e => updateItemRow(idx, 'quantity', parseInt(e.target.value) || 1)} className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm text-center" placeholder="Qty" />
                        </div>
                        {form.items.length > 1 && (
                          <button type="button" onClick={() => removeItemRow(idx)} className="text-red-400 hover:text-red-600 px-1 text-sm font-bold">✕</button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Tanggal Assign</label>
                  <input type="date" value={form.assigned_date} onChange={e => setForm({ ...form, assigned_date: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Estimasi Kembali</label>
                  <input type="date" value={form.expected_return_date} onChange={e => setForm({ ...form, expected_return_date: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                <div className="flex gap-2 flex-wrap">
                  {ASSIGN_STATUSES.map(s => (
                    <button type="button" key={s} onClick={() => setForm({ ...form, status: s })} className={`px-3 py-1.5 text-xs rounded-full font-medium transition-colors ${form.status === s ? (s === 'active' ? 'bg-blue-600 text-white' : s === 'returned' ? 'bg-green-600 text-white' : s === 'lost' ? 'bg-red-600 text-white' : 'bg-yellow-600 text-white') : (statusColors[s] || 'bg-gray-100 text-gray-600')}`}>
                      {s === 'active' ? 'Aktif' : s === 'returned' ? 'Kembali' : s === 'lost' ? 'Hilang' : 'Rusak'}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Catatan</label>
                <textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Batal</button>
                <button type="submit" disabled={saving} className="px-4 py-2 bg-brand text-white rounded-lg text-sm hover:bg-brand-dark disabled:opacity-50">{saving ? 'Menyimpan...' : 'Simpan'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}